export interface GeneratedContentItem {
  title: string;
  message: string;
  cvp: string;
  score: number;
  reviewResult?: string;
  reviewReason?: string;
}

export interface GenerationContext {
  country: string;
  endDate: string;
  language: string;
  vertical: string;
  product: string;
  startDate: string;
}

export interface GenerationRequest {
  attribute: string;
  payload: Record<string, any>;
}

export const buildContentConfigGenerationRequests = ({
  attributes,
  country,
  endDate,
  language,
  productUUID,
  startDate,
  vertical,
}: {
  attributes: string[];
  country: string;
  endDate: string;
  language: string;
  productUUID: string;
  startDate: string;
  vertical: string;
}): GenerationRequest[] => attributes.map((attribute) => ({
  attribute,
  payload: {
    countryCode: country,
    endDate,
    language,
    productUUID,
    segmentName: attribute,
    segmentType: 'attributepairing',
    startDate,
    vertical,
  },
}));

const parseMystiqueV2Items = (response: any = {}): GeneratedContentItem[] => {
  if (!response || !Array.isArray(response.payloads)) return [];

  return response.payloads
    .filter((payload) => payload && !payload.error)
    .map((payload) => ({
      title: payload.title || 'Untitled',
      message: payload.message || '',
      cvp: payload.cvp || '',
      score: (payload.review && payload.review.score) || 0,
      reviewResult: payload.review && payload.review.result,
      reviewReason: payload.review && payload.review.reason,
    }));
};

export const parseContentConfigGenerationResults = (
  attributes: string[] = [],
  responsesByAttribute: Record<string, any> = {},
): Record<string, GeneratedContentItem[]> => {
  return attributes.reduce((result, attribute) => ({
    ...result,
    [attribute]: parseMystiqueV2Items(responsesByAttribute[attribute]),
  }), {});
};

const csvCell = (value: any) => {
  const rawValue = String(value === undefined || value === null ? '' : value);
  const safeValue = /^[\s\u0000-\u001F]*[=+\-@]/.test(rawValue) ? `'${rawValue}` : rawValue;
  return `"${safeValue.replace(/"/g, '""')}"`;
};

export const buildContentConfigGenerationCsv = (
  context: GenerationContext,
  attributes: string[],
  itemsByAttribute: Record<string, GeneratedContentItem[]>,
) => {
  const rows: any[][] = [[
    'country',
    'language',
    'vertical',
    'product',
    'attribute',
    'start_date',
    'end_date',
    'cvp',
    'score',
    'title',
    'message',
  ]];

  attributes.forEach((attribute) => {
    (itemsByAttribute[attribute] || []).forEach((item) => {
      rows.push([
        context.country,
        context.language,
        context.vertical,
        context.product,
        attribute,
        context.startDate,
        context.endDate,
        item.cvp,
        item.score,
        item.title,
        item.message,
      ]);
    });
  });

  return `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`;
};

export const selectMissingContentConfigGenerationRequests = (
  requests: GenerationRequest[] = [],
  readyAttributes: string[] = [],
) => {
  const readyAttributeSet = new Set(readyAttributes);
  return requests.filter(({ attribute }) => !readyAttributeSet.has(attribute));
};

export const contentGenerationFilename = (
  product: string,
  attribute?: string,
) => {
  const slug = (value: string) => String(value || 'content')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug(product)}-${attribute ? slug(attribute) : 'all-attributes'}-content.csv`;
};

export const downloadContentConfigGenerationCsv = (
  filename: string,
  content: string,
) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};
